"use client";

import { MapPin } from "lucide-react";
import { useState } from "react";
import EditableCard from "@/src/components/cards/editable-card";
import LocationForm from "@/src/components/cards/location-form";
import { toast } from "@/src/hooks/use-toast";
import { useLocationCreateOrUpdateMutation, useLocations } from "@/src/lib/data-fetchers/locations-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { convertEmptyStringValuesToNull } from "@/src/lib/utils/object-utils";
import { Address, addressShape } from "@/src/models/address";
import { Location, LocationFieldKey, LocationErrors, validateLocation } from "@/src/models/location";

// List of fields to validate for this card
const locationFieldsToValidate: (keyof Location)[] = ["name", "phoneNumber", "faxPhoneNumber", "role"];
const addressFieldsToValidateKeys: (keyof Address)[] = ["line1", "line2", "city", "state", "postalCode", "country"];
const addressFieldsToValidate: `address.${keyof Address}`[] = addressFieldsToValidateKeys.map(key => `address.${key}` as const);
const fieldsToValidate: LocationFieldKey[] = [...locationFieldsToValidate, ...addressFieldsToValidate];
interface LocationCardEditableProps {
  location: Location;
  onClose: () => void;
  type?: string;
  // Callback for when user switches to a different location - allows parent to handle the context update
  onEditLocation?: (updatedLocation: Location) => Promise<void>;
  onEditLocationLoading?: boolean;
  createMode?: boolean;
}
export default function LocationCardEditable({
  location,
  onClose,
  type,
  onEditLocation,
  onEditLocationLoading = false,
  createMode = false
}: LocationCardEditableProps) {
  const [formData, setFormData] = useState<Location>({
    ...location
  });
  const [errors, setErrors] = useState<LocationErrors>({});
  const createOrUpdateLocationMutation = useLocationCreateOrUpdateMutation();
  const {
    locations,
    isLoading: isLocationsLoading
  } = useLocations();
  const handleInputChange = (field: LocationFieldKey, value: string) => {
    // Handle nested address fields (e.g., "address.line1")
    if (field.startsWith("address.")) {
      const addressField = field.replace("address.", "") as keyof Address;
      setFormData(prev => ({
        ...prev,
        address: prev.address ? {
          ...prev.address,
          [addressField]: value
        } : {
          ...addressShape,
          [addressField]: value
        }
      }));

      // Clear error for this field when user starts typing
      if (errors.address && addressField in errors.address) {
        setErrors(prev_0 => ({
          ...prev_0,
          address: (() => {
            if (!prev_0.address) return prev_0.address;
            const {
              [addressField]: _,
              ...rest
            } = prev_0.address;
            return rest;
          })()
        }));
      }
    } else {
      // Handle top-level fields
      const locationField = field as keyof Location;
      setFormData(prev_1 => ({
        ...prev_1,
        [locationField]: value
      }));

      // Clear error for this field when user starts typing
      setErrors(prev_2 => {
        const {
          [locationField]: _,
          ...rest_0
        } = prev_2;
        return rest_0;
      });
    }
  };
  const handleSave = async () => {
    const processedFormData = convertEmptyStringValuesToNull(formData);
    if (createMode) {
      processedFormData.isApproved = true;
    }
    const {
      isValid,
      errors: errors_0
    } = validateLocation(processedFormData, fieldsToValidate);
    setErrors(errors_0);
    if (!isValid) {
      return;
    }
    if (createMode && processedFormData.placeId && locations) {
      const existingLocationWithSamePlaceIdRoleAndEhrId = locations.find((existingLocation: Location) => existingLocation.placeId === processedFormData.placeId && existingLocation.role === processedFormData.role && existingLocation.ehrId === processedFormData.ehrId);
      if (existingLocationWithSamePlaceIdRoleAndEhrId) {
        setErrors(prev_3 => ({
          ...prev_3,
          placeId: "A location with the same place, role, and EHR ID already exists. Please select a different location."
        }));
        return;
      }
    }
    try {
      // If onEdit callback is provided, use it to update the parent context's location reference
      if (onEditLocation && processedFormData.id !== location.id) {
        await onEditLocation(processedFormData);
      }

      // Create or update location entity
      const savedLocation = await createOrUpdateLocationMutation.mutateAsync(processedFormData);

      // Update formData with the saved location to reflect any server-side changes
      setFormData(savedLocation);
      onClose?.();
      toast({
        title: createMode ? "Location created" : "Location updated",
        description: createMode ? "The location was successfully created." : "The location's information was successfully saved.",
        variant: "default"
      });
    } catch (error) {
      logger.error("Error saving location:", {
        error
      });
    }
  };
  const handleLocationSelected = (selectedLocation: Location | null) => {
    if (selectedLocation) {
      setErrors({});
      setFormData(selectedLocation);
    }
  };
  return <EditableCard title={createMode ? "Create New Location" : `Edit ${type ? `${type} Location` : "Location"}`} icon={MapPin} initialData={location} onCancel={onClose} onSave={handleSave} isSaving={createOrUpdateLocationMutation.isPending || onEditLocationLoading} createMode={createMode}>
      <LocationForm location={formData} onChange={(field_0: LocationFieldKey, value_0: string) => handleInputChange(field_0, value_0)} errors={errors} locations={locations} isLocationsLoading={isLocationsLoading} onLocationSelected={handleLocationSelected} showLocationMatcher={!!onEditLocation} createMode={createMode} />
    </EditableCard>;
}